import { DateTime } from "luxon";

import type { Call } from "@/types/call.types";
import { CallAnsweredByType } from "@/types/call.types";
import type { Team, TeamMember } from "@/types/team.types";

import type {
    ReachabilityAgentRow,
    ReachabilityCounts,
    ReachabilityHourlyPoint,
    ReachabilityOutcome,
    ReachabilitySummary,
} from "../types/reachability-analysis.types";

export const createEmptyReachabilityCounts = (): ReachabilityCounts => ({
    reached: 0,
    voicemail: 0,
    not_reached: 0,
});

export const classifyCallReachability = (answeredBy: CallAnsweredByType | null | undefined): ReachabilityOutcome => {
    if (answeredBy === CallAnsweredByType.HUMAN) {
        return "reached";
    }

    if (answeredBy === CallAnsweredByType.MACHINE_START) {
        return "voicemail";
    }

    return "not_reached";
};

const incrementOutcome = (counts: ReachabilityCounts, outcome: ReachabilityOutcome) => {
    counts[outcome] += 1;
};

export const buildReachabilitySummary = (calls: Call[]): ReachabilitySummary => {
    const counts = createEmptyReachabilityCounts();

    calls.forEach((call) => {
        incrementOutcome(counts, classifyCallReachability(call.answeredBy));
    });

    const total = calls.length;
    const reachRate = total > 0 ? (counts.reached / total) * 100 : 0;

    return {
        ...counts,
        total,
        reach_rate: reachRate,
    };
};

const getCallHour = (call: Call): number => {
    const timestamp = call.callStartTime ?? call.createdAt;
    return DateTime.fromISO(timestamp).hour;
};

const formatHourLabel = (hour: number): string => `${hour.toString().padStart(2, "0")}:00`;

export const buildReachabilityHourlySeries = (calls: Call[]): ReachabilityHourlyPoint[] => {
    const byHour = new Map<number, Call[]>();

    calls.forEach((call) => {
        const hour = getCallHour(call);
        const existing = byHour.get(hour) ?? [];
        existing.push(call);
        byHour.set(hour, existing);
    });

    return [...byHour.entries()]
        .sort(([leftHour], [rightHour]) => leftHour - rightHour)
        .map(([hour, hourCalls]) => {
            const summary = buildReachabilitySummary(hourCalls);

            return {
                hour,
                hourLabel: formatHourLabel(hour),
                reached: summary.reached,
                voicemail: summary.voicemail,
                not_reached: summary.not_reached,
                total: summary.total,
                reach_rate: summary.reach_rate,
            };
        });
};

export const getTeamMemberUserId = (member: TeamMember): string => member.user?.id ?? member.id;

export const getTeamMemberLabel = (member: TeamMember): string =>
    member.user?.name ?? member.user?.email ?? getTeamMemberUserId(member);

export const buildReachabilityAgentRows = (team: Team | null, calls: Call[]): ReachabilityAgentRow[] => {
    if (!team) {
        return [];
    }

    const callsByUser = new Map<string, Call[]>();

    calls.forEach((call) => {
        const existing = callsByUser.get(call.userId) ?? [];
        existing.push(call);
        callsByUser.set(call.userId, existing);
    });

    return team.members
        .map((member) => {
            const agentId = getTeamMemberUserId(member);
            const agentCalls = callsByUser.get(agentId) ?? [];
            const summary = buildReachabilitySummary(agentCalls);

            return {
                agentId,
                agentLabel: getTeamMemberLabel(member),
                ...summary,
            };
        })
        .sort((left, right) => right.total - left.total);
};
